'use client';

import type { ReactNode } from 'react';

import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useMemo } from 'react';

import {
  AppRouterContextProvider,
  type AppNavigate,
  type AppRouterContextValue,
} from '@vs-shop/rcprofi/shared/router/AppRouterContext';

import { installEngineShellPlatformClient } from '@rcprofi/next/runtime/engineShellClientBootstrap';

// Deterministic engine boot (workspace#54): install the client ShellPlatform when
// this root client-boundary module is evaluated. React evaluates a client
// boundary's module before rendering the boundary, and renders the boundary before
// any descendant whose render performs the first atom read — so this happens-before
// that read by construction, not by chunk-scheduling luck. See
// engineShellClientBootstrap for the full ordering argument.
installEngineShellPlatformClient();

/**
 * Next App Router implementation of the framework-free router seam
 * (`@vs-shop/rcprofi/shared/router/AppRouterContext`). This is the ONLY client module
 * allowed to import `next/navigation` (enforced by the WS8 boundary lint on
 * `component/**` + `container/**`; this file lives shell-side in `src/next/`).
 *
 * Only `useRouter()` is invoked here — it returns a stable object and does NOT
 * trigger Next's dynamic-render / CSR bailout. `usePathname` and
 * `useSearchParams` are passed through as function references and invoked by
 * the consumer hooks at their own call sites, preserving Next's per-call-site
 * bailout scope 1:1 (see AppRouterContext).
 */
export function AppRouterProvider({ children }: { children: ReactNode }) {
  const router = useRouter();

  const navigate = useMemo<AppNavigate>(
    () => ({
      push: (href) => router.push(href),
      replace: (href) => router.replace(href),
      refresh: () => router.refresh(),
      back: () => router.back(),
      prefetch: (href) => router.prefetch(href),
    }),
    [router]
  );

  const value = useMemo<AppRouterContextValue>(
    // Must pass the hook FUNCTION REFS, never call them here — calling
    // useSearchParams in the provider would opt the whole tree out of SSR.
    // No test guards this; the function-typed context field does.
    () => ({ navigate, usePathname, useSearchParams }),
    [navigate]
  );

  return <AppRouterContextProvider value={value}>{children}</AppRouterContextProvider>;
}
